// Platform-console namespace — RUSSIAN (spec 15 §4).
//
// Real translation, not a placeholder: src/i18n-completeness.ts claims `ru`
// complete for every namespace, and tools/ci/i18n-parity.ts check 4 fails the
// build if any value here is the English one (the two that legitimately are —
// the product's own name and the recovery-code file name — are declared in
// IDENTICAL_BY_DESIGN with their reason).
//
// Register: administrative Russian as an internal operations console would be
// written, not consumer UI Russian — «двухфакторная аутентификация», «код
// подтверждения», «резервные коды», «сессия».

import type { Translated } from "../../i18n-shape.js";
import type { adminEn } from "./admin.i18n.js";
import { adminOpsRu } from "./admin-ops.ru.js";
import { adminAgentsRu } from "./admin-agents.ru.js";

export const adminRuDict: Translated<typeof adminEn> = {
  brand: "Assessly",
  console: "Консоль платформы",

  notFound: {
    title: "Такой страницы в консоли нет",
    body: "Проверьте адрес или вернитесь ко входу.",
    cta: "Перейти ко входу",
  },

  // M15-4 (spec 15 §4 S9, S15; D-611 to D-613): tenant health and the logs.
  health: {
    nav: "Состояние",
    title: "Состояние арендаторов",
    lede: "Все активные арендаторы, худшие сверху, по последнему окну.",
    windowMeta: "Окно {{time}}",
    loading: "Загружаем состояние…",
    none: "—",
    open: "Открыть состояние",
    colTenant: "Арендатор",
    colHealth: "Состояние",
    colUptime: "Доступность за 24 ч",
    colErrors: "Доля ошибок",
    colP95: "Задержка p95",
    colLag: "Отставание событий",
    colParked: "Отложенные события",
    colAlerts: "Открытые оповещения",
    colKind: "Оповещение",
    colOpened: "Открыто",
    colState: "Статус",
    colPaged: "Вызов дежурного",
    alertKind: {
      canary_down: "Не отвечает",
      queue_lag: "События отстают",
      backup_stale: "Резервная копия устарела",
      fleet_collapse: "Сбой всей платформы",
    },
    dot: {
      green: "В норме",
      amber: "Ухудшение",
      red: "Недоступен",
      grey: "Нет данных",
    },
    reasons: {
      down: "Не ответил на проверку",
      alert_open: "Есть открытое оповещение",
      error_rate: "Доля ошибок 1 % и выше",
      latency: "Задержка p95 от 2 с",
      queue_lag: "События отстают на минуту и больше",
      parked_events: "Есть отложенные события",
      stale: "Нет данных за последние 15 минут",
      not_probed: "Не активен, поэтому не проверяется",
      health_drill: "Идёт учебная проверка",
    },
    noWindowTitle: "Данных пока нет",
    noWindowBody: "Сборщик записывает первое окно в течение десяти минут после запуска.",
    stale:
      "Последнее окно старше 15 минут. Пока сборщик не отработает снова, красными остаются только арендаторы с открытым оповещением.",
    statProbed: "Проверено арендаторов",
    statDown: "Недоступны сейчас",
    statAlerts: "Открытые оповещения",
    statBackup: "Последняя копия",
    backupOff: "Здесь не отслеживается",
    backupNone: "Не найдена",
    backupStale: "Старше 26 часов",
    tableTitle: "Активные арендаторы",
    emptyTitle: "Нет активных арендаторов",
    emptyBody: "Арендатор появится здесь после завершения настройки.",
    tenantTitle: "Состояние: {{name}}",
    drillOn: "Идёт учебная проверка: арендатор намеренно отвечает на проверку как недоступный.",
    now: "Последнее окно",
    noReadingTitle: "Данных по арендатору пока нет",
    noReadingBody: "Данные появятся с первым окном после активации арендатора.",
    probe: "Проверка",
    probeUp: "Ответил за {{ms}}",
    probeDown: "Не ответил",
    requests: "Запросов за окно",
    noTraffic: "Нет трафика",
    last24h: "Последние 24 часа",
    stripUp: "Доступность",
    up: "Ответил",
    down: "Не ответил",
    notCollected: "Не собрано",
    stripSummary: "Плохо: {{bad}} · внимание: {{warn}} · нет данных: {{none}} · окон: {{total}}",
    last30d: "Последние 30 дней",
    noDays: "Ни один полный день ещё не подытожен.",
    uptime: "Доступность",
    daysSummary: "Дней с данными: {{count}}",
    alertsTitle: "Оповещения",
    viewLogs: "Открыть журнал",
    noAlerts: "По этому арендатору оповещений нет.",
    alertOpen: "Открыто",
    alertResolved: "Закрыто {{time}}",
    paged: "Дежурный вызван",
    held: "Удержано, без вызова",
    drillTitle: "Учебная проверка",
    drillBody:
      "Учебная проверка заставляет этого тестового арендатора отвечать как недоступный, чтобы проверить панель и оповещения. Дежурный не вызывается.",
    drillFailed: "Учебная проверка не изменилась. Попробуйте ещё раз.",
    drillStart: "Начать учебную проверку",
    drillStop: "Остановить учебную проверку",
    drillStartBody:
      "Со следующего окна арендатор будет недоступен, а его оповещения откроются без вызова дежурного.",
    drillStopBody: "Со следующего окна арендатор снова будет отвечать на проверку.",
    cancel: "Отмена",
    saving: "Сохраняем…",
    confirm: "Подтвердить",
  },
  logs: {
    nav: "Журнал",
    title: "Журнал",
    lede: "Ошибки сервера и оповещения, новые сверху.",
    loading: "Загружаем журнал…",
    colTime: "Время",
    colTenant: "Арендатор",
    colStatus: "Код",
    colRoute: "Запрос",
    colCode: "Код проблемы",
    colDuration: "Длительность",
    colRequest: "ID запроса",
    host: {
      tenant: "Арендатор",
      admin: "Консоль",
      other: "Другой адрес",
    },
    fleet: "Вся платформа",
    errors: "Ошибки сервера",
    alerts: "Оповещения о состоянии",
    noErrorsTitle: "Ошибок сервера нет",
    noErrorsBody: "За этот период сервер не отвечал ошибкой.",
    noAlertsTitle: "Оповещений нет",
    noAlertsBody: "Сборщик не открывал подходящих оповещений.",
    oneTenant: "Один арендатор",
    allTenants: "Показать всех арендаторов",
    since: "Период",
    since1h: "Последний час",
    since24h: "Последние 24 часа",
    since7d: "Последние 7 дней",
    state: "Оповещения",
    stateAll: "Открытые и закрытые",
    stateOpen: "Только открытые",
  },

  // Plans, entitlements, usage and tenant edits (spec 15 §4 S5/S6/S7/S11/S12;
  // M15-6, M15-7a).
  tenantTabs: {
    label: "Разделы клиента",
    overview: "Обзор",
    entitlements: "Права доступа",
    usage: "Использование",
  },
  createPlan: {
    label: "Тариф",
    loading: "Загрузка тарифов…",
    failed: "Не удалось загрузить тарифы",
    none: "Ни один действующий тариф не включает этот отраслевой пакет.",
    refused: "Выбранный тариф нельзя назначить этому клиенту. Выберите другой.",
  },
  plans: {
    nav: "Тарифы",
    title: "Тарифы",
    create: "Новый тариф",
    colName: "Тариф",
    colCode: "Код",
    colStatus: "Статус",
    colPrice: "Годовая цена",
    colTenants: "Клиенты",
    colModules: "Модули",
    colActions: "Действия",
    priceHidden: "Скрыто для вашей роли",
    sandbox: "Песочница",
    showRetired: "Показать снятые тарифы",
    loading: "Загрузка тарифов…",
    emptyTitle: "Тарифов нет",
    emptyBody: "Тариф определяет модули и лимиты клиента.",
    emptyBodyAdmin: "Создайте тариф, чтобы задать модули и лимиты клиента.",
    edit: "Изменить",
    retire: "Снять",
    retireTitle: "Снять тариф {{name}}?",
    retireBody: "Клиенты на этом тарифе сохранят его. Новым клиентам его назначить нельзя.",
    retireConfirm: "Снять тариф",
    retiring: "Снимаем…",
    editorCreate: "Новый тариф",
    editorEdit: "Изменить {{name}}",
    code: "Код",
    codeHint: "Строчные латинские буквы, цифры и дефисы. После сохранения код не меняется.",
    name: "Название",
    price: "Годовая цена",
    currency: "Валюта",
    dunningDays: "Приостановить через дней без оплаты",
    notes: "Примечания",
    modules: "Модули",
    limits: "Лимиты",
    limitValue: "Лимит",
    warnPct: "Предупреждать при %",
    blockPct: "Блокировать при %",
    affects: "Клиентов на этом тарифе: {{count}}. Изменение коснётся каждого из них.",
    save: "Сохранить тариф",
    saving: "Сохраняем…",
    cancel: "Отмена",
    close: "Закрыть",
    failed: "Не удалось сохранить тариф. Попробуйте ещё раз.",
    codeTaken: "Этот код уже занят другим тарифом.",
    thresholds: "Порог предупреждения должен быть ниже порога блокировки, оба от 1 до 100.",
    retiredEdit: "Снятый тариф изменить нельзя.",
    pilotRequired: "Тариф Pilot нельзя снять: на нём все клиенты без своего тарифа.",
    refusedScope: "Ваш доступ не распространяется на этот тариф.",
    status: {
      active: "Действует",
      grandfathered: "Сохранён для старых клиентов",
      retired: "Снят",
    },
  },
  moduleCodes: {
    catalogue: "Каталог",
    onboarding: "Запуск",
    inventory: "Склад",
    waste: "Отходы",
    production: "Производство",
    quality: "Качество",
    planning: "Планирование",
    sales: "Продажи и CRM",
    messaging: "Сообщения",
    agents: "ИИ-агенты",
  },
  limitCodes: {
    max_facilities: "Площадки",
    max_users_soft: "Пользователи",
    storage_gb: "Хранилище (ГБ)",
    api_requests_day: "Запросов к API в день",
  },
  enforcement: {
    hard: "Жёсткий лимит",
    soft: "Мягкий лимит",
    fair_use: "Разумное использование",
    soft_then_hard: "Сначала предупреждение, затем блокировка",
  },
  entitlements: {
    packCode: "Отраслевой пакет: {{pack}}",
    loading: "Загрузка прав доступа…",
    plan: "Тариф",
    changePlan: "Сменить тариф",
    modules: "Модули",
    limits: "Лимиты",
    moduleCol: "Модуль",
    planCol: "По тарифу",
    overrideCol: "Исключение",
    effectiveCol: "Действует",
    actionsCol: "Действия",
    limitCol: "Лимит",
    usageCol: "Использовано",
    on: "Вкл.",
    off: "Выкл.",
    none: "Нет",
    noLimit: "Без лимита",
    noUsage: "Не измеряется",
    usedOf: "{{used}} из {{limit}}",
    until: "До {{date}}",
    noExpiry: "Без срока",
    overrideAdd: "Исключение",
    overrideEdit: "Изменить исключение",
    overrideTitle: "Исключение: {{name}}",
    switchOn: "Включить для этого клиента",
    value: "Значение лимита",
    reason: "Причина",
    reasonHint: "Не менее 5 символов. Сохраняется в журнале аудита.",
    expires: "Действует до",
    expiresHint: "Оставьте пустым, чтобы исключение действовало до отмены.",
    invalid: "Проверьте значение и дату окончания.",
    failed: "Не удалось сохранить исключение. Попробуйте ещё раз.",
    save: "Сохранить исключение",
    remove: "Отменить исключение",
    removing: "Отменяем…",
    planDialogTitle: "Сменить тариф клиента {{name}}",
    planSelect: "Новый тариф",
    planPick: "Выберите тариф",
    noOtherPlan: "Другого тарифа для отраслевого пакета этого клиента нет.",
    belowUsage: "Новый лимит площадок ({{limit}}) ниже текущего использования ({{usage}}).",
    force: "Всё равно перевести. У клиента останется то, что уже есть.",
    belowUsageRefused: "Лимиты нового тарифа ниже текущего использования клиента.",
    sandboxOnly: "Тарифы песочницы только для клиентов песочницы.",
    packNotInPlan: "Этот тариф не включает отраслевой пакет клиента.",
    planFailed: "Не удалось сменить тариф. Попробуйте ещё раз.",
  },
  usage: {
    loading: "Загрузка данных об использовании…",
    month: "Месяц",
    monthTitle: "Итоги месяца",
    noRollup: "Итогов за этот месяц нет.",
    mau: "Активные пользователи за месяц",
    domainEvents: "Доменные события",
    storagePeak: "Пик хранилища",
    refreshed: "Обновлено",
    daily: "Последние 90 дней",
    exportCsv: "Скачать CSV",
    csvDate: "Дата",
    metric: {
      active_users: "Активные пользователи",
      domain_events: "Доменные события",
      storage_bytes: "Хранилище",
    },
    sparkLabel: "{{metric}} по дням, с {{from}} по {{to}}",
    noReading: "Нет данных",
    window: "Дневные показания с {{from}} по {{to}}. Пустой день не измерялся.",
    emptyTitle: "Использование не измерено",
    emptyBody: "Использование измеряется каждый час. Данные появятся здесь после первого замера.",
  },
  tenantEdit: {
    edit: "Изменить данные",
    subdomain: "Сменить поддомен",
    plan: "Тариф",
    colPlan: "Тариф",
    colMau: "Активных за месяц",
    title: "Изменить {{name}}",
    name: "Название",
    locale: "Язык по умолчанию",
    timezone: "Часовой пояс",
    currencyLocked: "Валюту здесь изменить нельзя.",
    failed: "Не удалось сохранить изменения. Попробуйте ещё раз.",
    save: "Сохранить изменения",
    subdomainTitle: "Сменить поддомен {{name}}",
    newSubdomain: "Новый поддомен",
    subdomainPreview: "Новый адрес: {{host}}",
    subdomainRule:
      "От 3 до 63 строчных латинских букв, цифр или дефисов; начало и конец — буква или цифра.",
    reason: "Причина",
    subdomainHint:
      "Сначала предупредите клиента: после смены его пользователи входят по новому адресу.",
    subdomainTaken: "Этот поддомен занят другим клиентом.",
    subdomainReserved: "Этот поддомен зарезервирован.",
    subdomainFailed: "Не удалось сменить поддомен. Попробуйте ещё раз.",
    subdomainSave: "Сменить поддомен",
  },

  guard: {
    checking: "Проверяем вашу сессию…",
    errorTitle: "Не удалось связаться с консолью платформы",
    errorBody: "Проверка сессии не прошла. Обычно это сетевая ошибка — попробуйте ещё раз.",
    retry: "Повторить",
  },

  accept: {
    title: "Задайте пароль",
    lead: "Задайте пароль, затем настройте двухфакторный вход.",
    password: "Новый пароль",
    passwordHint: "Не меньше 12 символов. Надёжнее всего — фраза из нескольких несвязанных слов.",
    confirm: "Повторите пароль",
    show: "Показать пароль",
    hide: "Скрыть пароль",
    submit: "Задать пароль",
    submitting: "Задаём пароль…",
    mismatch: "Пароли не совпадают.",
    tooShort: "Используйте не меньше 12 символов.",
    invalidToken: "Эта ссылка-приглашение больше не действует. Запросите новое приглашение.",
    missingToken: "Этой странице нужна ссылка-приглашение из письма — откройте саму ссылку.",
    missingTokenHelp:
      "Ссылка неполная — так бывает при ручном копировании. Запросите новое приглашение или войдите, если пароль уже задан.",
    goSignIn: "Перейти ко входу",
    weak: "Такой пароль слишком легко угадать. Возьмите фразу подлиннее.",
    failed: "Не удалось задать пароль. Попробуйте ещё раз.",
    requestId: "Идентификатор запроса",
    strengthLabel: "Надёжность пароля",
    strength: {
      none: "—",
      weak: "Слабый",
      fair: "Средний",
      good: "Хороший",
      strong: "Надёжный",
    },
    strengthNote: "Это только подсказка — пароль проверяет сервер.",
  },

  login: {
    title: "Вход",
    lead: "Только для сотрудников Assessly. Каждой учётной записи нужна двухфакторная аутентификация.",
    passwordSet: "Пароль задан. Войдите, чтобы завершить настройку двухфакторной аутентификации.",
    sessionExpired: "Сессия истекла. Войдите заново, чтобы продолжить.",
    email: "Электронная почта",
    password: "Пароль",
    submit: "Войти",
    submitting: "Выполняется вход…",
    failed: "Такая пара почты и пароля не подошла. Попробуйте ещё раз.",
    locked:
      "Слишком много неудачных попыток. Учётная запись заблокирована минимум на 15 минут — подождите и попробуйте снова.",
    error: "Войти не удалось. Попробуйте ещё раз.",
    throttled: "Слишком много попыток входа. Повторите {{wait}}.",
    throttledLater: "Слишком много попыток входа. Подождите минуту и попробуйте снова.",
    totpTitle: "Код двухфакторной проверки",
    totpLead: "Введите шестизначный код из приложения-аутентификатора.",
    recoveryLead: "Введите один из сохранённых резервных кодов. Каждый работает один раз.",
    code: "Код подтверждения",
    codeHint: "Шесть цифр из приложения, которое вы подключили при настройке.",
    codeFormat: "Код из приложения — ровно шесть цифр.",
    verify: "Проверить",
    verifying: "Проверяем…",
    totpFailed:
      "Код не принят, а на этот шаг даётся одна попытка. Введите почту и пароль заново, затем используйте текущий код из приложения.",
    recoveryFailed:
      "Резервный код не принят. Каждый код работает один раз. Введите почту и пароль, затем возьмите другой код из списка.",
    recoveryFormat: "Резервный код выглядит так: ABCDE-FGHJK — десять символов и дефис.",
    useRecovery: "Ввести резервный код",
    useCode: "Ввести код из приложения",
    recoveryCode: "Резервный код",
    recoveryHint:
      "Один из десяти кодов, которые вы сохранили при настройке двухфакторной аутентификации.",
    expired: "Этот шаг входа истёк. Введите почту и пароль заново.",
    forgot: "Забыли пароль?",
    passwordReset: "Пароль изменён. Войдите с ним и кодом двухфакторной аутентификации.",
  },

  forgot: {
    title: "Сброс пароля",
    lead: "Введите рабочую почту. Если к ней привязана учётная запись консоли, мы пришлём ссылку для сброса.",
    email: "Электронная почта",
    submit: "Отправить ссылку",
    submitting: "Отправка…",
    sentTitle: "Проверьте почту",
    sent: "Если у этого адреса есть учётная запись консоли, ссылка для нового пароля уже в пути. Она работает один раз, 30 минут.",
    sentNote: "Если вы не завершили приглашение, вместо ссылки придёт приглашение.",
    error: "Не удалось отправить запрос. Попробуйте ещё раз.",
    backToSignIn: "Вернуться ко входу",
  },

  reset: {
    title: "Новый пароль",
    lead: "Задайте новый пароль. Вход по-прежнему потребует код двухфакторной аутентификации.",
    password: "Новый пароль",
    passwordHint: "Не меньше 12 символов. Надёжнее всего фраза из нескольких несвязанных слов.",
    confirm: "Повторите пароль",
    show: "Показать пароль",
    hide: "Скрыть пароль",
    submit: "Задать новый пароль",
    submitting: "Задаём пароль…",
    mismatch: "Пароли не совпадают.",
    tooShort: "Используйте не меньше 12 символов.",
    invalid: "Эта ссылка для сброса больше не действует.",
    invalidHelp:
      "Ссылка работает один раз, 30 минут, и новая ссылка заменяет старую. Запросите новую.",
    requestNew: "Запросить новую ссылку",
    missingToken: "Для этой страницы нужна ссылка из письма. Откройте саму ссылку.",
    weak: "Этот пароль слишком легко угадать. Попробуйте фразу подлиннее.",
    failed: "Не удалось задать пароль. Попробуйте ещё раз.",
    requestId: "Идентификатор запроса",
    goSignIn: "Перейти ко входу",
  },

  enroll: {
    title: "Настройте двухфакторную аутентификацию",
    lead: "Отсканируйте это приложением-аутентификатором и введите его код.",
    loading: "Готовим код настройки…",
    errorTitle: "Не удалось начать настройку",
    errorBody: "Код настройки не сгенерирован. Попробуйте ещё раз.",
    retry: "Повторить",
    qrAlt: "QR-код для приложения-аутентификатора",
    qrUnavailable:
      "Этот код слишком длинный, чтобы нарисовать его здесь. Добавьте секрет ниже вручную.",
    secretLabel: "Или введите секрет вручную",
    secretHelp: "Впишите его в приложение как ключ по времени (TOTP).",
    copySecret: "Скопировать секрет",
    copied: "Скопировано",
    code: "Шестизначный код",
    confirm: "Подтвердить",
    confirming: "Подтверждаем…",
    failed: "Этот код не принят. Дождитесь следующего и попробуйте снова.",
    notChecked:
      "Этот код не удалось проверить. Он не отклонён, настройка аутентификатора в порядке: попробуйте тот же код или следующий.",
    sessionExpired:
      "Сессия истекла до того, как код был проверен, — код не был отклонён. Войдите заново, чтобы продолжить настройку.",
    requestId: "Идентификатор запроса",
    privacy: "Секрет остаётся в вашем браузере и в приложении-аутентификаторе.",
  },

  recovery: {
    title: "Сохраните резервные коды",
    warningTitle: "Они показываются один раз и больше никогда.",
    warningBody:
      "Каждый код позволяет войти один раз, если телефон потерян. Сохраните их сейчас: показать их снова мы не сможем.",
    listLabel: "Десять одноразовых резервных кодов",
    copy: "Скопировать коды",
    copied: "Скопировано",
    download: "Скачать текстовым файлом",
    fileName: "assessly-platform-recovery-codes.txt",
    fileHeader: "Консоль платформы Assessly — одноразовые резервные коды",
    ack: "Я сохранил эти коды там, где смогу их взять без телефона.",
    ackRequired: "Подтвердите, что коды сохранены, прежде чем продолжить.",
    continue: "Перейти в консоль",
  },

  reissue: {
    title: "Резервные коды",
    body: "Потеряли резервные коды? Выпустите новый набор. Старые коды перестанут работать; понадобится приложение-аутентификатор.",
    start: "Выпустить новые резервные коды",
    code: "Код из приложения",
    codeHint: "Шесть цифр из приложения — чтобы подтвердить, что это вы.",
    confirm: "Выпустить новые коды",
    working: "Выпускаем…",
    cancel: "Отмена",
    failed: "Этот код не принят. Дождитесь следующего и попробуйте снова.",
    format: "Код из приложения — ровно шесть цифр.",
    notChecked:
      "Этот код не удалось проверить, но он не отклонён. Попробуйте тот же код или следующий из приложения.",
    sessionExpired: "Сессия истекла, поэтому код не был проверен. Войдите заново.",
    requestId: "Идентификатор запроса",
    doneTitle: "Ваши новые резервные коды",
    doneLead: "Они заменяют все выпущенные раньше. Старые коды больше не работают.",
    back: "Вернуться к учётной записи",
  },

  home: {
    title: "Ваша учётная запись",
    lead: "Ваш вход, роль и резервные коды.",
    email: "Электронная почта",
    role: "Роль",
    staffId: "Идентификатор сотрудника",
    session: "Сессия",
    sessionPolicy: "Выход через 60 минут без действий или через 12 часов в любом случае.",
    twoFactor: "Двухфакторная аутентификация",
    twoFactorOn: "Подтверждена",
    twoFactorOff: "Не настроена",
    signOut: "Выйти",
    signingOut: "Выходим…",
    signOutFailed: "Не удалось выйти корректно. Для надёжности закройте браузер.",
  },

  nav: {
    overview: "Обзор",
    label: "Консоль",
    tenants: "Все клиенты",
    account: "Ваша учётная запись",
  },
  navGroups: {
    fleet: "Парк",
    tenants: "Клиенты",
    support: "Поддержка",
    health: "Состояние",
    usage: "Использование и тарифы",
    staff: "Сотрудники и аудит",
    account: "Учётная запись",
  },
  shell: {
    menu: "Меню",
    env: {
      production: "Рабочая среда",
      staging: "Тестовая среда",
      local: "Локальная среда",
    },
    sandboxScope: "песочница",
    notFoundBody: "Проверьте адрес или вернитесь к обзору.",
    goHome: "Перейти к обзору",
  },
  account: {
    identity: "Данные входа",
  },
  fleet: {
    title: "Обзор парка",
    refresh: "Обновить",
    refreshing: "Обновляем…",
    updated: "Обновлено {{when}}",
    loading: "Загружаем данные парка…",
    partial: "Часть показателей не удалось прочитать. Обновите страницу.",
    attentionTitle: "Требует внимания",
    attentionEmpty: "Ничего не требует внимания.",
    attentionMore: "Показано {{shown}} из {{total}}.",
    recentTitle: "Последние изменения",
    recentEmpty: "Ни один клиент ещё не менял статус.",
    since: "С {{when}}",
    tiles: {
      tenants_active: "Активные клиенты",
      tenants_provisioning: "Настраиваются",
      tenants_suspended: "Приостановлены",
      tenants_provision_failed: "Настройка не удалась",
      staff_signed_in: "Сотрудники в системе",
    },
    attention: {
      provision_failed: "Настройка не удалась",
      provisioning_stalled: "Настройка зависла",
    },
  },

  status: {
    active: "Работает",
    provisioning: "Настраивается",
    provision_failed: "Настройка не удалась",
    suspended: "Приостановлен",
    pending_deletion: "Удаление назначено",
    exporting: "Выгрузка данных",
    purging: "Удаляется",
    purged: "Удалён",
  },

  reasons: {
    billing_overdue: "Просрочена оплата",
    compliance: "Требования регулятора",
    tenant_request: "По просьбе клиента",
    security_incident: "Инцидент безопасности",
  },

  modes: {
    grace: "Только чтение на 7 дней",
    immediate: "Заблокировать сразу",
  },

  regions: {
    "ap-southeast-1": "Сингапур",
    "eu-central-1": "Франкфурт",
    "me-central-1": "ОАЭ",
    "af-south-1": "Кейптаун",
  },

  steps: {
    seed_baseline: "Создать рабочее пространство",
    pack_seed: "Загрузить отраслевой пакет",
    dns_check: "Проверить веб-адрес",
    invite_admin: "Пригласить администратора",
    activate: "Включить",
  },

  stepState: {
    done: "Готово",
    skipped: "Пропущено",
    pending: "Ожидает",
  },

  load: {
    failed: "Не удалось загрузить.",
    retry: "Повторить",
    requestId: "Идентификатор запроса",
    forbidden: "Вашей роли это недоступно.",
  },

  tenants: {
    title: "Клиенты",
    create: "Новый клиент",
    search: "Поиск",
    searchPlaceholder: "Название или поддомен",
    status: "Статус",
    allStatuses: "Все",
    loading: "Загружаем клиентов…",
    colName: "Название",
    colSubdomain: "Поддомен",
    colStatus: "Статус",
    colPack: "Отрасль",
    colRegion: "Регион",
    colCreated: "Создан",
    sandbox: "Песочница",
    emptyTitle: "Клиентов пока нет",
    emptyBody: "Создайте первого, чтобы у компании появилось рабочее пространство.",
    emptyBodyViewer: "Клиентов создаёт администратор платформы.",
    noMatchTitle: "Ничего не найдено",
    noMatchBody: "Попробуйте другое название или статус.",
    clearFilters: "Сбросить фильтры",
    page: "Страница {{page}}",
    prev: "Предыдущая страница",
    next: "Следующая страница",
  },

  tenant: {
    back: "Клиенты",
    loading: "Загружаем клиента…",
    notFoundTitle: "Такого клиента нет",
    notFoundBody: "Возможно, адрес набран с ошибкой. Вернитесь к списку.",
    overview: "Обзор",
    subdomain: "Веб-адрес",
    openSite: "Открыть",
    pack: "Отрасль",
    region: "Регион",
    locale: "Язык",
    currency: "Валюта",
    timezone: "Часовой пояс",
    created: "Создан",
    adminEmail: "Администратор",
    resendInvite: "Отправить приглашение ещё раз",
    resending: "Отправляем…",
    resent: "Приглашение отправлено на {{email}}.",
    resendFailed: "Не удалось отправить приглашение.",
    suspension: "Приостановка",
    suspendedFor: "Причина",
    suspendedMode: "Режим",
    suspendedAt: "С",
    graceEnds: "Только чтение до",
    setup: "Настройка",
    attempts: "Попыток",
    lastError: "Последняя ошибка",
    retry: "Повторить настройку",
    retrying: "Повторяем…",
    retryFailed: "Не удалось перезапустить настройку.",
    suspend: "Приостановить",
    restore: "Восстановить",
    history: "История",
    historyEmpty: "Событий пока нет.",
    historyMore: "Показать раньше",
    createdEvent: "Создан",
    historyFailed: "Не удалось загрузить историю.",
    by: "{{who}}",
    automatic: "автоматически",
    byStaff: "сотрудник платформы",
    refusedRole: "Вашей роли это недоступно.",
    refusedScope: "Сотрудники песочницы не могут менять клиентов вне песочницы.",
    sandboxOnly: "Сотрудники песочницы могут просматривать этого клиента, но не изменять его.",
    changedFirst: "Клиента уже изменил кто-то другой. Здесь его текущее состояние.",
  },

  suspend: {
    title: "Приостановить {{name}}",
    reason: "Причина",
    mode: "Что произойдёт",
    note: "Комментарий (необязательно)",
    confirm: "Приостановить",
    working: "Приостанавливаем…",
    cancel: "Отмена",
    close: "Закрыть",
    failed: "Не удалось приостановить клиента.",
  },

  restore: {
    title: "Восстановить {{name}}",
    body: "Пользователи сразу смогут войти и продолжить работу.",
    note: "Комментарий (необязательно)",
    confirm: "Восстановить",
    working: "Восстанавливаем…",
    cancel: "Отмена",
    close: "Закрыть",
    failed: "Не удалось восстановить клиента.",
  },

  create: {
    title: "Новый клиент",
    stepLabel: "Шаг {{n}} из {{total}}",
    stepIdentity: "Название",
    stepPack: "Отрасль",
    stepDefaults: "Настройки",
    stepAdmin: "Администратор",
    name: "Название компании",
    namePlaceholder: "ТОО «КазКабель»",
    subdomain: "Поддомен",
    subdomainHint: "Строчные латинские буквы, цифры и дефисы.",
    checking: "Проверяем…",
    available: "{{host}} свободен.",
    taken: "Уже занят.",
    reserved: "Зарезервирован. Выберите другое имя.",
    invalid:
      "От 3 до 63 строчных латинских букв, цифр или дефисов; дефис не в начале и не в конце.",
    checkFailed: "Не удалось проверить имя. Создать клиента всё равно можно попробовать.",
    pack: "Отраслевой пакет",
    packsLoading: "Загружаем пакеты…",
    packsFailed: "Не удалось загрузить пакеты.",
    region: "Регион хранения данных",
    locale: "Язык по умолчанию",
    currency: "Валюта",
    currencyHint: "После первых денежных записей валюту не изменить.",
    timezone: "Часовой пояс",
    adminEmail: "Почта администратора",
    adminEmailHint: "Ему придёт приглашение настроить рабочее пространство.",
    emailInvalid: "Введите полный адрес почты.",
    review: "Проверьте и создайте",
    back: "Назад",
    next: "Далее",
    submit: "Создать клиента",
    creating: "Создаём…",
    cancel: "Отмена",
    failed: "Не удалось создать клиента.",
    noAnswer: "Ответ не пришёл. Попробуйте ещё раз: второй клиент не появится.",
    refusedRole: "Вашей роли нельзя создавать клиентов.",
    takenOnSubmit: "Этот поддомен только что заняли. Выберите другой.",
    reservedOnSubmit: "Этот поддомен зарезервирован. Выберите другой.",
    forbiddenTitle: "Создавать клиентов может только администратор платформы.",
    forbiddenBody: "Обратитесь к администратору или вернитесь к списку.",
    progressTitle: "Настраиваем {{name}}",
    progressLive: "{{name}} работает.",
    progressFailed: "Настройка остановилась.",
    openTenant: "Открыть {{host}}",
    viewTenant: "Открыть карточку клиента",
  },

  roles: {
    platform_admin: "Администратор платформы",
    platform_support: "Поддержка платформы",
    platform_billing: "Биллинг платформы",
    platform_readonly: "Только просмотр",
  },
  // M15-8 and M15-9 (D-620 to D-623).
  ...adminOpsRu,
  ...adminAgentsRu,
};
